import { BUILD_VERSION } from "src/config";

import type { VintedItemUnavailableError } from "./VintedClient";

/**
 * How much of the failed item-page response to keep on the inventory row. The
 * 404 body is a generic Next.js shell (~18 KB); the useful part for triage is
 * the status and the wardrobe flags already on the row, so a snippet is enough.
 */
export const VintedPageFailureBodySnippetLength = 2048;

/** Import run the failure belongs to. */
export interface VintedPageFailureContext {
  syncRunId: string;
  job: "inventory-import" | "daily-sync";
  importType: string;
  trigger: string;
  /** Wardrobe API page (1-based) the item came from, when known. */
  page?: number;
}

/**
 * Per-run info attached to an inventory row whose item page could not be
 * fetched. The backend `vinted-processFile` posts one Slack alert per file that
 * contains any `pageFailure`, so the row must stay self-describing.
 */
export interface VintedPageFailure {
  status: number;
  responseUrl: string | null;
  errorName: string;
  errorMessage: string;
  bodyBytes: number;
  bodySnippet: string;
  fetchedAt: string;
  syncRunId: string;
  job: VintedPageFailureContext["job"];
  importType: string;
  trigger: string;
  clientVersion: string;
  page?: number;
}

/**
 * Annotate a wardrobe row with the failure instead of dropping it: the item is
 * still imported (title/price/photos/brand come from the row) and the mapping
 * functions ignore this extra property.
 */
export function annotateVintedInventoryPageFailure(
  item: Record<string, unknown>,
  error: VintedItemUnavailableError,
  context: VintedPageFailureContext
): Record<string, unknown> {
  const body = String(error.body ?? "");
  const failure: VintedPageFailure = {
    status: error.status,
    responseUrl: error.responseUrl || null,
    errorName: error.name,
    errorMessage: error.message,
    bodyBytes: body.length,
    bodySnippet: body.slice(0, VintedPageFailureBodySnippetLength),
    fetchedAt: new Date().toISOString(),
    syncRunId: context.syncRunId,
    job: context.job,
    importType: context.importType,
    trigger: context.trigger,
    clientVersion: BUILD_VERSION,
  };
  if (context.page != null) failure.page = context.page;

  return { ...item, pageFailure: failure };
}
